var f = require("../scripts/f.js");
exports.CreateBrand = CreateBrand;
/**
 * Creates a style object that is used by the e-mail builder.
 * Also used and stored to generate the CSS for HTML viewer.
 * @param       {*} brand   [The brand color - must be a HEX code]
 * @param       {*} textcolor [The text color - must be a HEX code]
 * @param       {*} bg        [The BG Object created from "Create Backhrond"]
 * @param       {*} logo      [The path to a PNG or SVG (plz dear god) to the logo.]
 * @constructor
 */
function CreateBrand(brandcolor, textcolor, bg, logo) {
  if (f.ValidateHEX(brandcolor) == false) {
    return {
      success: false,
      error: "The Brand Color is not a valid 6 character HEX Code.",
      fault: "Hex brandcolor"
    };
  }

  if (f.ValidateHEX(textcolor) == false) {
    return {
      success: false,
      error: "The Text Color is not a valid 6 character HEX Code.",
      fault: "Hex textcolor"
    };
  }

  if ("success" in bg) {
    //use the CreateBackground item
  } else {
    return {
      success: false,
      error: "The BG object reported a fault",
      fault: "BG Object Fault",
      pure: bg
    };
  }


  return {
    success: true,
    brandcolor: brandcolor,
    textcolor: textcolor,
    background: bg,
    logo: logo
  };
}

exports.CreateBackground = CreateBackground;
function CreateBackground(bgcolor, image = false) {

  if (f.ValidateHEX(bgcolor) == false) {
    return {
      success: false,
      error: "The Background Color (bgcolor) is not a valid 6 character HEX Code.",
      fault: "Hex bgcolor"
    };
  }

  return {
    success: true,
    color: bgcolor,
    image: image
  }
}

exports.CreateBrandFromConfig = CreateBrandFromConfig;

// NEUTRAL FALLBACKS — the tenant-neutral contract (agents.md non-negotiable: the
// framework must NEVER impose EGT's black/gold/serif on a tenant whose config does
// not ask for it). Every EGT-specific value lives in EGT's def.config.json, NOT
// here. A tenant with no fonts/accent/surface keys gets these neutrals instead:
//   - body/heading font -> Arial (a neutral sans-serif; NEVER a serif stack)
//   - accent            -> the tenant's own primary (NEVER gold)
//   - surfaces          -> light neutral panels (NEVER a forced dark/black surface)
var NEUTRAL_BODY_FONT = "Arial,Helvetica,sans-serif";
// SEAM / FOUNDER OPEN QUESTION (email.card light surfaceMode, deferred in M4):
// email.card is "dark-only by construction" — leaf children ink their own text
// WHITE from my.app.colors.text, which beats any surface a parent sets, so a light
// neutral surface + white child text would be invisible. A full LIGHT surfaceMode
// (that also flips child text colour) is a design decision the founder still owns.
// Until then these neutral surfaces guarantee only that the FALLBACK is neutral
// (no black leaks onto another tenant); they are not a finished light theme. A
// non-dark tenant should set my.app.surfaces (and eventually a surfaceMode) itself.
var NEUTRAL_SURFACES = {
  surface: "#F4F4F5",
  panel: "#FFFFFF",
  raise: "#FAFAFA",
  ink: "#E9E9EB",
  border: "#E5E5E5"
};

/**
 * Creates a brand object from the app config ($config).
 * Reads my.app.colors.primary, my.app.colors.text, my.app.colors.background,
 * and my.app.url to build the logo URL. Also reads the extended, config-driven
 * brand axes (fonts, accent, surfaces, surfaceMode) added for the shared email
 * contract — each with a NEUTRAL fallback so a tenant that omits them never
 * inherits EGT's black/gold/serif. EGT fills these from def.config.json, so its
 * rendered output is unchanged.
 * @param {object} $config  The app config object (from uam/scafolding.js $.config)
 * @returns {object} Brand object (CreateBrand output + fonts/accent/surfaces).
 */
function CreateBrandFromConfig($config) {
  var app     = ($config.my && $config.my.app) ? $config.my.app : {};
  var colors  = app.colors || {};
  var fonts   = app.fonts || {};
  var surface = app.surfaces || {};

  var primary = colors.primary || "#6C757D";
  var text    = colors.text    || "#FFFFFF";
  var bg      = colors.background || "#000000";
  var appUrl  = app.url ? app.url : "";
  var logo    = appUrl.replace(/\/$/, "") + "/logo.png";

  // body -> Arial neutral; heading -> body when unset (NEVER a serif default).
  var bodyFont    = fonts.body || NEUTRAL_BODY_FONT;
  var headingFont = fonts.heading || bodyFont;
  // accent -> the tenant's own primary when unset (NEVER gold).
  var accent      = colors.accent || primary;
  var surfaces = {
    surface: surface.surface || NEUTRAL_SURFACES.surface,
    panel:   surface.panel   || NEUTRAL_SURFACES.panel,
    raise:   surface.raise   || NEUTRAL_SURFACES.raise,
    ink:     surface.ink     || NEUTRAL_SURFACES.ink,
    border:  surface.border  || NEUTRAL_SURFACES.border
  };
  // surfaceMode is the DEFERRED seam (see NEUTRAL_SURFACES note). Carried on the
  // brand for downstream use; email.card does not yet branch on it.
  var surfaceMode = app.surfaceMode || "neutral";

  var brand = CreateBrand(primary, text, CreateBackground(bg), logo);
  if (brand && brand.success) {
    brand.bodyFont = bodyFont;
    brand.headingFont = headingFont;
    brand.accent = accent;
    brand.surfaces = surfaces;
    brand.surfaceMode = surfaceMode;
  }
  return brand;
}

/**
 * The optional "paper" email theme (my.app.emailTheme). A tenant that sets it gets
 * its email body on a light sheet over the brand canvas, with its own ink, accent
 * and fonts: the header and footer stay on the canvas, the body sits on the sheet.
 * A tenant without it gets null, and every email element keeps its existing output
 * byte for byte (each element branches on this, never on its own guess).
 *
 *   "emailTheme": {
 *     "mode": "paper", "width": 600, "sheet": "#EFE6D3", "radius": 18,
 *     "ink": "#120D06", "ink2": "#463C2A", "muted": "#5A4A30", "rule": "#D6C9AE",
 *     "accent": "#C48E2F", "accentInk": "#7A5410", "onAccent": "#0C0C0C",
 *     "fonts": { "body": "...", "heading": "...", "ui": "..." }, "fontsHref": "https://fonts.googleapis.com/css2?..."
 *   }
 *
 * @param {object} [$config] Defaults to the live app config.
 * @returns {object|null}
 */
function emailTheme($config) {
  if (!$config) { try { $config = require("../../../uam/scafolding.js").config || {}; } catch (e) { $config = {}; } }
  var app = ($config && $config.my && $config.my.app) ? $config.my.app : {};
  var t = app.emailTheme;
  if (!t || typeof t !== "object" || t.mode !== "paper") { return null; }
  var colors = app.colors || {};
  var f = t.fonts || {};
  var serif = "Georgia,'Times New Roman',serif";
  var sans = "Helvetica,Arial,sans-serif";
  return {
    mode: "paper",
    width: (t.width > 0 ? t.width : 600),
    sheet: t.sheet || "#FFFFFF",
    radius: (typeof t.radius === "number" ? t.radius : 16),
    ink: t.ink || "#111111",
    ink2: t.ink2 || t.ink || "#333333",
    muted: t.muted || "#666666",
    rule: t.rule || "#DDDDDD",
    accent: t.accent || colors.primary || "#6C757D",
    accentInk: t.accentInk || t.accent || colors.primary || "#6C757D",
    onAccent: t.onAccent || "#FFFFFF",
    bodyFont: f.body || serif,
    headingFont: f.heading || f.body || serif,
    uiFont: f.ui || sans,
    fontsHref: (typeof t.fontsHref === "string" && /^https:\/\/fonts\.googleapis\.com\//.test(t.fontsHref)) ? t.fontsHref : ""
  };
}
exports.emailTheme = emailTheme;
